import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { apiClient, ENDPOINTS } from '../services/apiConfig';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

// Function to format AI responses
const formatAIResponse = (text: string): string => {
  // Remove markdown symbols (* and #)
  let formatted = text.replace(/[*#]/g, '');
  
  // Ensure proper paragraph spacing
  formatted = formatted.replace(/\n\s*\n/g, '\n\n'); // Normalize paragraph breaks
  
  return formatted;
};

const Chat = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Hello! I'm your Mood Friend. How are you feeling today?",
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: messages.length + 1,
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      // Get conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Add the new user message
      conversationHistory.push({
        role: 'user',
        content: input
      });

      // Call AI API
      //const response = await axios.post('http://localhost:5000/api/ai/chat', {
      const response = await apiClient.post(`${ENDPOINTS.AI}/chat`, {
        messages: conversationHistory,
      });
      
      // Format the AI response and add AI message
      const formattedResponse = formatAIResponse(response.data.message || "I'm sorry, I couldn't process that. Could you try again?");
      
      const aiMessage: Message = {
        id: messages.length + 2,
        text: formattedResponse,
        sender: 'ai',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: messages.length + 2,
        text: "I'm having trouble connecting right now. Please try again later.",
        sender: 'ai',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Function to render message text with paragraph formatting
  const renderMessageText = (text: string, sender: 'user' | 'ai') => {
    if (sender === 'user') {
      return (
        <Typography
          variant="body1"
          sx={{
            fontFamily: { xs: '"Quicksand", sans-serif', md: '"Lato", sans-serif' },
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: { xs: 1.55, sm: 1.6 },
            overflowWrap: 'anywhere',
          }}
        >
          {text}
        </Typography>
      );
    }
    // For AI messages, split by double newlines to create paragraphs
    const paragraphs = text.split('\n\n');

    return (
      <>
        {paragraphs.map((paragraph, index) => (
          <Typography
            key={index}
            variant="body1"
            paragraph={index < paragraphs.length - 1}
            sx={{
              mt: index > 0 ? 1 : 0,
              fontFamily: { xs: '"Quicksand", sans-serif', md: '"Lato", sans-serif' },
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: { xs: 1.55, sm: 1.6 },
              overflowWrap: 'anywhere',
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </>
    );
  };

  return (
    <>
      <Container
        maxWidth="md"
        disableGutters
        sx={{ mt: { xs: 0, md: 4 }, mb: { xs: 0, md: 4 }, px: { sm: 2 } }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1.5, sm: 2 },
            height: { xs: 'calc(100dvh - 160px)', sm: 'calc(100vh - 170px)', md: 'calc(100vh - 180px)' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 0, sm: 2 }, // flush edges on phones
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid #eee', fontFamily: '"Lato", sans-serif', fontSize: { xs: 18, sm: 20 } }}
          >
            Chat with your Mood Friend about your thoughts and feelings
          </Typography>

          {/* Messages area */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
            <List>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      maxWidth: { xs: '92%', md: '70%' },
                      flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                      gap: { xs: 1, sm: 1.5 },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: message.sender === 'user' ? 'primary.main' : 'secondary.main',
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        mr: message.sender === 'user' ? 0 : 0,
                        ml: message.sender === 'user' ? 0 : 0,
                      }}
                    >
                      {message.sender === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                    </Avatar>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: message.sender === 'user' ? 'primary.light' : 'background.paper',
                        borderRadius: 2,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {renderMessageText(message.text, message.sender)}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1, fontFamily: '"Quicksand", sans-serif' }}
                      >
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
              ))}
              {loading && (
                <ListItem sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', maxWidth: '70%' }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 1 }}>
                      <SmartToyIcon />
                    </Avatar>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <CircularProgress size={20} />
                    </Paper>
                  </Box>
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Input area */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                placeholder="Type your message..."
                variant="outlined"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              />
              <IconButton color="primary" sx={{ ml: 1 }}>
                <MicIcon />
              </IconButton>
              <Button
                variant="contained"
                color="primary"
                endIcon={<SendIcon />}
                onClick={handleSend}
                sx={{ ml: 1 }}
              >
                Send
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default Chat;